import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MessageCircle,
  Clock,
  CheckCircle,
  Plus,
} from "lucide-react";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import useODODetail from "../../hooks/cs/useODODetail";
import { useAuthStore } from "../../store/authStore";
import ODOCommentForm from "../../components/cs/ODOCommentForm";

const ODODetail = () => {
  const { odoId } = useParams<{ odoId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { odoDetail, isLoading, error, fetchODODetail } = useODODetail();
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  console.log(odoDetail);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth/signin");
      return;
    }

    if (odoId) {
      fetchODODetail(odoId);
    }
  }, [odoId, isAuthenticated, fetchODODetail, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "답변대기":
        return {
          icon: <Clock className="w-4 h-4" />,
          color: "text-orange-600 bg-orange-100",
          text: "답변대기",
        };
      case "답변완료":
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          color: "text-green-600 bg-green-100",
          text: "답변완료",
        };
      default:
        return {
          icon: <MessageCircle className="w-4 h-4" />,
          color: "text-gray-600 bg-gray-100",
          text: status,
        };
    }
  };

  const formatDescription = (description: string) => {
    return description.split("\n").map((line, index) => (
      <div key={index}>
        {line}
        {index < description.split("\n").length - 1 && <br />}
      </div>
    ));
  };

  const parseImageUrls = (imageString: string): string[] => {
    if (!imageString || imageString.trim() === "") {
      return [];
    }
    return imageString
      .split(",")
      .map((data) => {
        const trimmedData = data.trim();
        if (
          !trimmedData.startsWith("http") &&
          !trimmedData.startsWith("data:")
        ) {
          return `data:image/jpeg;base64,${trimmedData}`;
        }
        return trimmedData;
      })
      .filter((url) => url !== "");
  };

  const handleCancel = () => {
    navigate("/cs", {
      state: { activeTab: "odo" },
      replace: true,
    });
  };

  const handleCommentSuccess = () => {
    setShowCommentForm(false);
    setShowSuccessMessage(true);

    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);

    if (odoId) {
      fetchODODetail(odoId);
    }
  };

  const handleCommentCancel = () => {
    setShowCommentForm(false);
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <main className="mx-auto py-[64px] md:py-[100px] px-4 md:px-6 relative max-w-4xl mb-[100px]">
          <LoadingSpinner message="문의 상세 내용을 불러오는 중..." size="lg" />
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <main className="mx-auto py-[64px] md:py-[100px] px-4 md:px-6 relative max-w-4xl mb-[100px]">
          <div className="text-center py-12">
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg"
            >
              목록으로 돌아가기
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!odoDetail) {
    return (
      <>
        <Header />
        <main className="mx-auto py-[64px] md:py-[100px] px-4 md:px-6 relative max-w-4xl mb-[100px]">
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">
              문의 내용을 찾을 수 없습니다.
            </p>
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg"
            >
              목록으로 돌아가기
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const statusInfo = getStatusInfo(odoDetail.base.process_status);

  return (
    <>
      <Header />
      <main className="mx-auto py-[64px] md:py-[100px] px-5 md:px-6 relative max-w-4xl mb-[100px] font-serif-kr">
        <div className="mb-6 px-2">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-gray-900 hover:text-blue-500 hover:underline transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>목록으로 돌아가기</span>
          </button>
        </div>

        {/* 문의 상세 정보 */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-6 md:px-8 py-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  {odoDetail.base.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>작성일: {formatDate(odoDetail.base.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.color}`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </span>
              </div>
            </div>
          </div>

          {/* 문의 내용 */}
          <div className="px-6 md:px-8 py-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              문의 내용
            </h2>

            {odoDetail.base.reference_image &&
              parseImageUrls(odoDetail.base.reference_image).length > 0 && (
                <div className="mb-6">
                  <div className={`rounded-xl grid grid-cols-1 ${parseImageUrls(odoDetail.base.reference_image).length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4`}>
                    {parseImageUrls(odoDetail.base.reference_image).map(
                      (imageUrl, index) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`문의 첨부 이미지 ${index + 1}`}
                          className="max-w-full h-auto rounded-lg border border-gray-200"
                          onError={(e) => {
                            console.error(`이미지 로드 실패: ${imageUrl}`);
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )
                    )}
                  </div>
                </div>
              )}

            <div className="prose max-w-none">
              <div className="leading-relaxed whitespace-pre-wrap">
                {formatDescription(odoDetail.base.description)}
              </div>
            </div>
          </div>

          {/* 답변 내용 */}
          {odoDetail.comments && odoDetail.comments.length > 0 && (
            <div className="border-t border-gray-200">
              <div className="px-6 md:px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    답변 내용
                  </h2>
                  {isAuthenticated && !showCommentForm && (
                    <button
                      onClick={() => setShowCommentForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      답글 작성
                    </button>
                  )}
                </div>

                {showSuccessMessage && (
                  <div className="mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <p className="text-green-700 font-medium">답글이 성공적으로 작성되었습니다.</p>
                      </div>
                    </div>
                  </div>
                )}

                {isAuthenticated && showCommentForm && (
                  <div className="mb-8">
                    <ODOCommentForm
                      inquiryId={odoId!}
                      onSuccess={handleCommentSuccess}
                      onCancel={handleCommentCancel}
                    />
                  </div>
                )}
                
                <div className="space-y-6">
                  {odoDetail.comments.map((comment, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${
                        comment.is_me
                          ? "bg-blue-50 border-blue-200 ml-8"
                          : "bg-green-50 border-green-200 mr-8"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.is_me ? "문의자" : "관리자"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>

                      {comment.reference_image &&
                        parseImageUrls(comment.reference_image).length > 0 && (
                          <div className="mb-3">
                            <div className={`grid grid-cols-1 ${parseImageUrls(comment.reference_image).length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"} gap-3`}>
                              {parseImageUrls(comment.reference_image).map(
                                (imageUrl, imgIndex) => (
                                  <img
                                    key={imgIndex}
                                    src={imageUrl}
                                    alt={`답변 첨부 이미지 ${imgIndex + 1}`}
                                    className="max-w-full h-auto rounded-lg border border-gray-200"
                                    onError={(e) => {
                                      console.error(
                                        `이미지 로드 실패: ${imageUrl}`
                                      );
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                )
                              )}
                            </div>
                          </div>
                        )}

                      <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {formatDescription(comment.description)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 답변이 없을 때 답글 작성 섹션 */}
          {(!odoDetail.comments || odoDetail.comments.length === 0) &&
            isAuthenticated && (
              <div className="border-t border-gray-200">
                <div className="px-6 md:px-8 py-6">
                  {!showCommentForm ? (
                    <div className="flex flex-col justify-center">
                      <div className="text-center pb-2">
                        <p className="text-gray-500 text-lg mb-2">
                          아직 답변이 등록되지 않았습니다.
                        </p>
                        <p className="text-gray-500 text-sm mb-2">
                          추가로 궁금한 점이 있으시면 답글을 남겨주세요.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowCommentForm(true)}
                        className="flex justify-center mx-auto items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        답글 작성
                      </button>
                    </div>
                  ) : (
                    <ODOCommentForm
                      inquiryId={odoId!}
                      onSuccess={handleCommentSuccess}
                      onCancel={handleCommentCancel}
                    />
                  )}
                </div>
              </div>
            )}

          <div className="px-6 md:px-8 py-6 border-t border-gray-200">
            <div className="flex justify-center">
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ODODetail;
